"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import useEmblaCarousel from "embla-carousel-react";
import Footer from "@/components/Footer";

const centers = [
  {
    id: "samut-prakan",
    highlightImage: "/images/hsrc/HSRC-Highlight-SP.jpg",
    phoneDisplay: "02 757 6111 ext 3005",
    phoneLink: "02-757-6111",
    carouselImages: [
      "/images/hsrc/HSRC-Carousel-SP-1.jpg",
      "/images/hsrc/HSRC-Carousel-SP-2.jpg",
      "/images/hsrc/HSRC-Carousel-SP-3.jpg",
      "/images/hsrc/HSRC-Carousel-SP-4.jpg",
    ],
  },
  {
    id: "bangkok",
    highlightImage: "/images/hsrc/HSRC-Highlight-BK.jpg",
    phoneDisplay: "02 735 3999 ext 3108",
    phoneLink: "02-735-3999",
    carouselImages: [
      "/images/hsrc/HSRC-Carousel-BK-1.jpg",
      "/images/hsrc/HSRC-Carousel-BK-2.jpg",
      "/images/hsrc/HSRC-Carousel-BK-3.jpg",
    ],
  },
  {
    id: "chiang-mai",
    highlightImage: "/images/hsrc/HSRC-Highlight-CM.jpg",
    phoneDisplay: "053 294 999",
    phoneLink: "053-294-999",
    carouselImages: [
      "/images/hsrc/HSRC-Carousel-CM-1.jpg",
      "/images/hsrc/HSRC-Carousel-CM-2.jpg",
      "/images/hsrc/HSRC-Carousel-CM-3.jpg",
    ],
  },
  {
    id: "phuket",
    highlightImage: "/images/hsrc/HSRC-Highlight-PK.jpg",
    phoneDisplay: "076 562 999",
    phoneLink: "076-562-999",
    carouselImages: [
      "/images/hsrc/HSRC-Carousel-PK-1.jpg",
      "/images/hsrc/HSRC-Carousel-PK-2.jpg",
      "/images/hsrc/HSRC-Carousel-PK-3.jpg",
      "/images/hsrc/HSRC-Carousel-PK-4.jpg",
    ],
  },
];

const centerNameKeys: Record<string, string> = {
  "samut-prakan": "samutPrakan",
  bangkok: "bangkok",
  "chiang-mai": "chiangMai",
  phuket: "phuket",
};

const centerTitleKeys: Record<string, string> = {
  "samut-prakan": "centerTitleSP",
  bangkok: "centerTitleBK",
  "chiang-mai": "centerTitleCM",
  phuket: "centerTitlePK",
};

const centerAddressKeys: Record<string, string> = {
  "samut-prakan": "samutPrakan",
  bangkok: "bangkok",
  "chiang-mai": "chiangMai",
  phuket: "phuket",
};

const centerHoursKeys: Record<string, string> = {
  "samut-prakan": "operationHours",
  bangkok: "operationHours",
  "chiang-mai": "operationHoursCM",
  phuket: "operationHoursPK",
};



const ensureMinSlides = (images: string[], min: number = 5): string[] => {
  if (images.length >= min) return images;
  const result: string[] = [];
  while (result.length < min) {
    result.push(...images);
  }
  return result;
};

export default function CenterHsrcPage() {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const centerParam = searchParams.get("center");
  const initialIndex = centerParam ? centers.findIndex((c) => c.id === centerParam) : 0;
  const validInitialIndex = initialIndex >= 0 ? initialIndex : 0;

  const [activeIndex, setActiveIndex] = useState(validInitialIndex);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    slidesToScroll: 1,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);



  const [mapUrls, setMapUrls] = useState<Record<string, string>>({});

  const activeCenter = centers[activeIndex];
  const locationName = t(`center.${centerNameKeys[activeCenter.id]}` as any);
  const centerTitle = t(`centerHsrc.${centerTitleKeys[activeCenter.id]}` as any);
  const locationAddress = t(`centerAddress.${centerAddressKeys[activeCenter.id]}` as any);

  const carouselImages = ensureMinSlides(activeCenter.carouselImages, 5);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    fetch('/api/centers/public', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.success && Array.isArray(data.data)) {
          const urlMap: Record<string, string> = {};
          for (const center of data.data) {
            const slug = (center.codeE || '').toLowerCase().replace(/\s+/g, '-');
            if (slug && center.mapUrl) {
              urlMap[slug] = center.mapUrl;
            }
          }
          setMapUrls(urlMap);
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return (
    <main className="min-h-screen bg-white">
      <section className="lg:hidden bg-white px-6 pt-6 pb-2">
        <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto w-full">
          <h1 className="text-[32px] font-bold text-dark leading-[32px]">
            {t("center.title")}
          </h1>
        </div>
      </section>
      <section className="hidden lg:block relative w-full overflow-hidden bg-white">
        <div className="relative w-full aspect-[8/1]">
          <Image
            src="/images/cover/cover-hsrc.jpg"
            alt="Hero Banner"
            fill
            className="object-cover object-center w-full"
            sizes="100vw"
            priority
          />
          <div className="absolute inset-0 px-6 lg:px-[clamp(2rem,5vw,250px)] flex items-center">
            <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto w-full">
              <h1 className="text-3xl md:text-4xl lg:text-[56px] font-bold text-white leading-tight md:leading-tight lg:leading-[56px]">
                {t("center.title")}
              </h1>
            </div>
          </div>
        </div>
      </section>

      <div className="hidden lg:block px-6 lg:px-[clamp(2rem,5vw,250px)] pt-[10px] pb-4">
        <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto">
          <nav className="flex items-center gap-2 text-xs text-dark flex-wrap">
            <a href="/" className="hover:text-honda-red transition-colors">
              {t("common.home")}
            </a>
            <img src="/images/icons/icon-breadcrumb.svg" alt="" className="w-[6px] h-[11px]" aria-hidden="true" />
            <span className="text-honda-red">
              {t("center.title")}
            </span>
          </nav>
        </div>
      </div>

      <section className="py-6 sm:py-8 lg:pt-9 lg:pb-12 bg-white">
        <div className="px-6 lg:px-[clamp(2rem,5vw,250px)]">
          <div className="max-w-[1920px] 2xl:max-w-[1440px] mx-auto">

          <div className="flex gap-1 sm:gap-2 md:gap-3 lg:gap-4 mb-6 justify-center">
            {centers.map((location) => {
              const isActive = activeCenter.id === location.id;
              return (
                <button
                  key={location.id}
                  onClick={() => {
                    const idx = centers.findIndex(
                      (c) => c.id === location.id
                    );
                    setActiveIndex(idx);
                  }}
                  className={`flex-1 min-w-0 py-2 sm:py-3 lg:py-3 rounded-lg text-[10px] sm:text-sm md:text-base lg:text-lg font-bold flex items-center justify-center gap-1 sm:gap-2 transition-all ${
                    isActive
                      ? "bg-white text-dark"
                      : "bg-white text-dark hover:text-honda-red"
                  }`}
                >
                  <Image
                    src={
                      isActive
                        ? "/images/icons/icon-center-orange.svg"
                        : "/images/icons/icon-center-gray.svg"
                    }
                    alt={t(`center.${centerNameKeys[location.id]}` as any)}
                    width={32}
                    height={40}
                    className="w-5 h-6 sm:w-6 sm:h-8 md:w-7 md:h-9 lg:w-8 lg:h-10 object-contain transition-all flex-shrink-0"
                  />
                  <span className="truncate">
                    {t(`center.${centerNameKeys[location.id]}` as any)}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mb-8 sm:mb-9">
            <div className="relative w-full aspect-[16/9] overflow-hidden rounded-xl">
              <Image
                key={activeCenter.highlightImage}
                src={activeCenter.highlightImage}
                alt={centerTitle}
                fill
                className="object-cover"
                quality={90}
              />
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 mb-8 sm:mb-10 lg:mb-12">
            <div className="w-full lg:w-[60%]">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-dark mb-4 sm:mb-5 lg:mb-6">
                {centerTitle}
              </h2>

              <div className="mb-6 sm:mb-8 max-w-[1420px]">
                <p className="text-sm sm:text-base leading-5 sm:leading-6 lg:leading-[22px] text-dark font-bold mb-2">
                  {t("center.objective")}
                </p>
                <p className="text-sm sm:text-base leading-5 sm:leading-6 lg:leading-[22px] text-dark">
                  {t("center.missionBody")}
                </p>
              </div>

              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-dark mb-4 sm:mb-5 lg:mb-6">
                {t("common.moreDetail")}
              </h3>
              <div className="space-y-3 sm:space-y-4 text-sm sm:text-base leading-5 sm:leading-6 lg:leading-[22px] text-dark">
                <p>
                  {t(`center.${centerHoursKeys[activeCenter.id] || "operationHours"}`)}
                </p>
              </div>
            </div>

            <div className="w-full lg:w-[40%] lg:min-w-[320px]">
              <div className="bg-white rounded-lg shadow-[0px_12px_40px_rgba(0,0,0,0.25)] overflow-hidden">
                <div className="bg-white px-4 sm:px-6 pt-4 sm:pt-5 pb-3 sm:pb-4 min-h-[80px] sm:h-[100px] flex items-center">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Image
                      src="/images/icons/icon-phone-red.svg"
                      alt="Phone"
                      width={36}
                      height={36}
                      className="w-7 h-7 sm:w-8 sm:h-8 lg:w-9 lg:h-9 flex-shrink-0"
                    />
                    <span className="text-xl sm:text-2xl lg:text-[32px] font-bold text-honda-red">
                      {activeCenter.phoneDisplay.includes(' ext ') ? (
                        <>
                          {activeCenter.phoneDisplay.split(' ext ')[0]} {t("common.extLabel")} {activeCenter.phoneDisplay.split(' ext ')[1]}
                        </>
                      ) : (
                        activeCenter.phoneDisplay
                      )}
                    </span>
                  </div>
                </div>

                <div className="bg-honda-red px-4 sm:px-6 py-4 sm:py-6">
                  <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <Image
                      src="/images/icons/icon-pin-white.svg"
                      alt="Location"
                      width={36}
                      height={36}
                      className="w-7 h-7 sm:w-8 sm:h-8 lg:w-9 lg:h-9 flex-shrink-0 mt-1"
                    />
                    <div className="text-white text-base sm:text-lg lg:text-xl leading-6 sm:leading-7 lg:leading-[28px]">
                      <p>{locationAddress}</p>
                    </div>
                  </div>
                  <a
                    href={mapUrls[activeCenter.id] || "#"}
                    target={mapUrls[activeCenter.id] ? "_blank" : undefined}
                    rel={mapUrls[activeCenter.id] ? "noopener noreferrer" : undefined}
                    className="flex items-center gap-2 sm:gap-3 text-white text-base sm:text-lg lg:text-xl hover:opacity-90 transition-opacity mt-3 sm:mt-4"
                  >
                    <Image
                      src="/images/icons/icon-pin-white.svg"
                      alt="Map"
                      width={24}
                      height={24}
                      className="w-7 h-7 sm:w-8 sm:h-8 lg:w-9 lg:h-9 flex-shrink-0 invisible"
                    />
                    <span>{t("common.map")}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {false && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-10 lg:mb-12">
              {[1, 2, 3, 4].map((item) => (
                <div key={item} className="flex items-start gap-3 sm:gap-4">
                  <Image
                    src="/images/icons/icon-bike-red.svg"
                    alt="Feature Icon"
                    width={36}
                    height={36}
                    className="w-7 h-7 sm:w-8 sm:h-8 lg:w-9 lg:h-9 flex-shrink-0"
                  />
                  <div className="text-sm sm:text-base leading-5 sm:leading-6 lg:leading-[22px] text-dark font-bold">
                    <p>Lorem ipsum dolor sit amet,</p>
                    <p>consectetuer adipiscing elit,</p>
                    <p>sed diam nonummy</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="w-full h-[3px] sm:h-[4px] lg:h-[5px] bg-honda-red mb-8 sm:mb-10 lg:mb-12"></div>

          <div className="mb-8 sm:mb-10 lg:mb-12">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 lg:gap-4 xl:gap-6 pb-4">
              <button
                type="button"
                onClick={scrollPrev}
                className="hidden sm:flex flex-shrink-0 w-6 h-6 items-center justify-center hover:scale-110 active:scale-90 transition-all duration-200 z-10"
                aria-label="Previous slide"
              >
                <Image
                  src="/images/icons/icon-next.svg"
                  alt="Previous"
                  width={32}
                  height={32}
                  className="w-6 h-6 object-contain rotate-90"
                  style={{ filter: 'brightness(0.133)' }}
                  unoptimized
                />
              </button>

              <div className="embla-hsrc flex-1 overflow-hidden w-full" ref={emblaRef}>
                <div className="embla-hsrc__container">
                  {carouselImages.map((src, index) => (
                    <div
                      key={`${activeCenter.id}-${index}`}
                      className="embla-hsrc__slide relative min-w-0 flex-[0_0_100%] lg:flex-[0_0_33.33%]"
                    >
                       <div
                        className={`aspect-[16/9] relative transition-all duration-300 ${
                          emblaApi && selectedIndex === index
                            ? "scale-100 opacity-100"
                            : "scale-85 opacity-70"
                        }`}
                        style={{ borderRadius: '1rem', overflow: 'hidden' }}
                      >
                        <Image
                          src={src}
                          alt={`${centerTitle} ${index + 1}`}
                          fill
                          className="object-cover"
                          sizes="100vw"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex sm:hidden items-center justify-between w-full mt-4">
                <button type="button" onClick={scrollPrev} className="w-8 h-8 flex items-center justify-center active:scale-90 transition-all">
                  <Image src="/images/icons/icon-next.svg" alt="Previous" width={32} height={32} className="w-8 h-8 object-contain rotate-90" style={{ filter: 'brightness(0.133)' }} unoptimized />
                </button>
                <button type="button" onClick={scrollNext} className="w-8 h-8 flex items-center justify-center active:scale-90 transition-all">
                  <Image src="/images/icons/icon-next.svg" alt="Next" width={32} height={32} className="w-8 h-8 object-contain -rotate-90" style={{ filter: 'brightness(0.133)' }} unoptimized />
                </button>
              </div>

              <button
                type="button"
                onClick={scrollNext}
                className="hidden sm:flex flex-shrink-0 w-6 h-6 items-center justify-center hover:scale-110 active:scale-90 transition-all duration-200 z-10"
                aria-label="Next slide"
              >
                <Image
                  src="/images/icons/icon-next.svg"
                  alt="Next"
                  width={32}
                  height={32}
                  className="w-6 h-6 object-contain -rotate-90"
                  style={{ filter: 'brightness(0.133)' }}
                  unoptimized
                />
              </button>
            </div>
          </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
